import { Inject, Injectable, Injector } from '@angular/core';
import { DataService } from '../../../core/service/base/data.service';
import { Representative } from '../../model/representative';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { HttpClient } from '@angular/common/http';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { Observable, catchError, map, switchMap, take, tap } from 'rxjs';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { SendRegistrationKeyFormRepresentativeComponent } from '../../component/send-registration-key-form-representative/send-registration-key-form-representative.component';
import { RepresentativeDeleteDialogComponent } from '../../component/representative-delete-dialog/representative-delete-dialog.component';

@Injectable()
export class RepresentativeService extends DataService<Representative> {
  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private messageService: MessageService,
    progressBar: ProgressBarService,
    private _actionService: ActionWindowService,
    private _injector: Injector
  ) {
    super(client, config, messageService, Representative, progressBar, 'representative');
  }

  getByLeaseAgreementId(leaseAgreementId: string): Observable<Representative[]> {
    return this.client
      .get<Representative[]>(`${this.endpoint}/lease-agreement/${leaseAgreementId}`)
      .pipe(map(items => items.map(item => Object.assign(new Representative(leaseAgreementId), item))));
  }

  getByContactAndLeaseAgreementId(leaseAgreementId: string, contactId: string): Observable<Representative> {
    return this.client
      .get<Representative>(`${this.endpoint}/lease-agreement/${leaseAgreementId}/contact/${contactId}`)
      .pipe(map(item => Object.assign(new Representative(leaseAgreementId), item)));
  }

  sendRegistrationKey(contactId: string, email: string, language?: string): Observable<void> {
    return this._actionService
      .open(SendRegistrationKeyFormRepresentativeComponent, this._injector, [
        { provide: 'defaultEmail', useValue: email },
        { provide: 'culture', useValue: language },
      ])
      .pipe(
        take(1),
        switchMap(result => {
          return this.client
            .post(
              `${this.config.tenantEndpoint}/send-key`,
              JSON.stringify({
                id: contactId,
                email: result.email,
                language: result.language,
              })
            )
            .pipe(
              map(_ => {
                this._actionService.close();
              }),
              catchError(error => {
                this.messageService.showError('REPRESENTATIVE.DIALOG.FAIL');
                return [];
              }),
              tap(() => this.messageService.showSuccess('SUCCESS.REGISTRATION_KEY_SENT'))
            );
        })
      );
  }

  deleteWithConfirmation(representativeId: string): Observable<void> {
    return this._actionService
      .open(RepresentativeDeleteDialogComponent, this._injector, [{ provide: 'id', useValue: representativeId }])
      .pipe(
        take(1),
        switchMap(result => {
          // TODO: result has option with delete contact, currently not operable
          return this.delete(representativeId).pipe(
            map(_ => {
              this._actionService.close();
            }),
            catchError(error => {
              this.messageService.showError('REPRESENTATIVE.DIALOG.FAIL');
              return [];
            }),
            tap(() => this.messageService.showSuccess('REPRESENTATIVE.DIALOG.DELETE_SUCCESS'))
          );
        })
      );
  }
}
